import type { ListBuilderColumn, ListBuilderConfiguration } from '../types';

/**
 * Recent Changes (PD-16593) — the reviewer's recent match decisions (confirm,
 * reject, skip, manual match, unmatch, undo), reachable from the Compliance
 * dashboard and the Matching page, so a match that just left the Matching
 * list can be found again, opened, and undone.
 *
 * Backed by GET /api/licenses/matches/changes: one row per
 * `client_license_match_log` decision, newest first. Every display value is
 * projected by the endpoint (who, what, the license as filed, the registry
 * record, whether the decision still stands) because config columns render
 * field values verbatim. Opens on the caller's own decisions over the last 7
 * days (Chris 2026-09-27); teammates and Workd Automation are filter toggles.
 */

const TABLE = 'client_license_match_log';

const secondary = {
	size: 'sm' as const,
	colorRule: { type: 'theme' as const, themeColor: 'textSecondary' as const },
};

const lineBreak = (id: string): ListBuilderColumn['fields'][number] => ({
	id,
	dataConfig: { type: 'custom_text' as const, customText: '' },
	displayStyle: 'line_break' as const,
});

const COLUMNS: Array<ListBuilderColumn> = [
	{
		id: 'change-when',
		fields: [
			{
				id: 'change-at-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'changedAt', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					weight: 'medium',
					colorRule: { type: 'default' },
					formatOptions: { dateFormat: 'datetime-short' },
				},
				displayStyle: 'datetime',
			},
			lineBreak('change-when-lb'),
			{
				// "You", a teammate's name, or "Workd Automation".
				id: 'change-who-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'whoLabel', objectTable: TABLE },
				},
				styleConfig: secondary,
				displayStyle: 'text',
			},
		],
		label: 'When',
		headers: [{ id: 'h-change-when', text: 'When' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		sortable: false,
		width: 150,
	},
	{
		id: 'change-action',
		fields: [
			{
				id: 'change-action-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'actionLabel', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					weight: 'medium',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
			lineBreak('change-note-lb'),
			{
				// A reject's reason, when one was given.
				id: 'change-note-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'note', objectTable: TABLE },
				},
				styleConfig: {
					...secondary,
					textExpansion: { enabled: true, maxLines: 1 },
				},
				displayStyle: 'text',
				conditionalVisibility: { field: 'note', condition: 'is_not_null' },
			},
		],
		label: 'Change',
		headers: [{ id: 'h-change-action', text: 'Change' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		sortable: false,
		minWidth: 170,
	},
	{
		id: 'change-account',
		fields: [
			{
				id: 'change-client-name-field',
				link: {
					type: 'custom',
					enabled: true,
					urlTemplate: '/clients/{clientId}',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: 'clientName', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					weight: 'medium',
					colorRule: { type: 'theme', themeColor: 'statusInfo' },
					textExpansion: { enabled: true, maxLines: 1 },
				},
				displayStyle: 'text',
			},
			lineBreak('change-client-lb'),
			{
				// Same C#/L# badge as the Matching list: the account's external id,
				// falling back to the internal id for a lead without one.
				id: 'change-client-badge',
				dataConfig: {
					type: 'template',
					template:
						'{clientLeadOrCustomer:Customer=C,Lead=L}#{clientExternalId2|clientExternalId|clientId}',
					templateFields: [
						{ fieldName: 'clientLeadOrCustomer' },
						{ fieldName: 'clientExternalId2' },
						{ fieldName: 'clientExternalId' },
						{ fieldName: 'clientId' },
					],
				},
				styleConfig: {
					size: 'xs',
					colorRule: {
						type: 'conditional',
						rules: [
							{
								field: { fieldName: 'clientLeadOrCustomer' },
								operator: 'equals',
								value: 'Customer',
								ignoreCase: true,
								color: { type: 'info' },
							},
							{
								field: { fieldName: 'clientLeadOrCustomer' },
								operator: 'equals',
								value: 'Lead',
								ignoreCase: true,
								color: { type: 'warning' },
							},
						],
					},
					variant: 'solid',
				},
				displayStyle: 'badge',
				conditionalVisibility: { field: 'clientId', condition: 'is_not_null' },
			},
		],
		label: 'Account',
		headers: [{ id: 'h-change-account', text: 'Account' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		sortable: false,
		minWidth: 200,
	},
	{
		id: 'change-license',
		fields: [
			{
				// The number on the customer's file before the decision.
				id: 'change-license-filed-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'licenseAsFiled', objectTable: TABLE },
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
			{
				id: 'change-license-arrow',
				dataConfig: { type: 'custom_text', customText: ' → ' },
				styleConfig: secondary,
				displayStyle: 'text',
			},
			{
				id: 'change-registry-record-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'registryRecord', objectTable: TABLE },
				},
				styleConfig: {
					size: 'sm',
					weight: 'medium',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
			lineBreak('change-registry-lb'),
			{
				id: 'change-registry-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'registryName', objectTable: TABLE },
				},
				styleConfig: {
					...secondary,
					textExpansion: { enabled: true, maxLines: 1 },
				},
				displayStyle: 'text',
			},
		],
		label: 'License → Registry',
		headers: [{ id: 'h-change-license', text: 'License → Registry' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		sortable: false,
		minWidth: 260,
	},
	{
		id: 'change-state',
		fields: [
			{
				// Read from the license's VALUES and the match's status, never a
				// modify time — a NetSuite echo does not read as a change.
				id: 'change-state-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'currentStateLabel', objectTable: TABLE },
				},
				styleConfig: {
					size: 'xs',
					colorRule: {
						type: 'conditional',
						rules: [
							{
								field: { fieldName: 'currentStateTone' },
								operator: 'equals',
								value: 'success',
								color: { type: 'success' },
							},
							{
								field: { fieldName: 'currentStateTone' },
								operator: 'equals',
								value: 'warning',
								color: { type: 'warning' },
							},
						],
					},
				},
				displayStyle: 'badge',
			},
		],
		label: 'Now',
		headers: [{ id: 'h-change-state', text: 'Now' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		sortable: false,
		width: 170,
	},
	{
		id: 'change-actions',
		fields: [
			{
				id: 'change-actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						// The license, or the review screen for a match still pending
						// (a skip, an undone decision).
						id: 'open-change-action',
						label: 'Open',
						iconName: 'arrowUpRightFromSquare',
						callbackName: 'openChange',
						variant: 'secondary',
						visibility: 'visible',
						showLabel: true,
					},
					{
						// Shown for every undoable decision; the click re-checks and
						// explains a refusal (the license changed since).
						id: 'undo-change-action',
						label: 'Undo',
						iconName: 'rotateLeft',
						callbackName: 'undoChange',
						variant: 'secondary',
						visibility: 'visible',
						showLabel: true,
						requiredPermission: 'licenses:update',
						conditionalVisibility: {
							field: 'undoKind',
							condition: 'equals',
							value: 'undo',
						},
					},
					{
						// PD-16602: in place of Undo on a confirm made by Workd
						// Automation — the undo also rejects the match, so the
						// nightly run does not confirm it again. Says so before the
						// click; the confirmation spells it out.
						id: 'undo-reject-change-action',
						label: 'Undo and reject',
						iconName: 'rotateLeft',
						callbackName: 'undoChange',
						variant: 'secondary',
						visibility: 'visible',
						showLabel: true,
						requiredPermission: 'licenses:update',
						conditionalVisibility: {
							field: 'undoKind',
							condition: 'equals',
							value: 'undo_and_reject',
						},
					},
				],
			},
		],
		label: '',
		headers: [{ id: 'h-change-actions', text: '' }],
		styleConfig: { size: 'md', colorRule: { type: 'default' } },
		alignment: 'right',
		sortable: false,
		// PD-16602: wide enough for Open beside "Undo and reject" (the wider of
		// the two Undo labels): ~241px of buttons + 24px cell padding, measured
		// at 1440px with the side menu open. At 190 Open was clipped.
		width: 280,
	},
];

export const LICENSE_MATCH_CHANGES_LIST_ID = 'license-match-changes-recent';

export const LICENSE_MATCH_CHANGES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: LICENSE_MATCH_CHANGES_LIST_ID,
			// Short: a decision just made must show up on the next load.
			cacheTTL: 0,
			name: 'Recent Changes',
			type: 'default',
			active: true,
			sortOrder: 0,
			// Defaults, not locks: a reviewer turns on teammates and Workd
			// Automation, or widens the window.
			filterRules: {
				changeWho: {
					defaultValue: [{ id: 'me', label: 'My changes' }],
				},
				changeWindow: {
					defaultValue: [{ id: 'week', label: 'Last 7 days' }],
				},
			},
			columns: COLUMNS,
			defaultSortId: 'newest-first',
		},
	],
	// The endpoint returns newest first and takes no other order.
	tableSorts: [
		{
			id: 'newest-first',
			name: 'Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'changedAt',
					objectTable: TABLE,
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'changeKeywords',
			'changeWho',
			'changeWindow',
			'changeAction',
		],
	},
};
